import type { SorterResult } from "antd/lib/table/interface";
import { type FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import type { ContractList } from "src/app/features/Contract/types/ContractList";
import { type ExtendedColumnsType, GenericTable, type ResourceTableProps } from "../../../../../components";
import {
    displayDateTimeValue,
    displayDateValue,
    displayEnumValue,
} from "../../../../../components/Table/components/PropertyTable/transformations/cellTransformations";
import { ResourceName, ResourceVariant } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../core/router/types/routerTypes";

export const ContractsTable: FC<ResourceTableProps<ContractList>> = ({ onStateChanged, onRowSelectionChange, datasource, resource }) => {
    const { t } = useTranslation();
    const getColumnsConfig = useCallback(
        (sortInfo: SorterResult<ContractList>): ExtendedColumnsType<ContractList>[] => {
            return [
                {
                    title: t("contract.id"),
                    key: "id",
                    dataIndex: "id",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "id" ? sortInfo.order : null,
                },
                {
                    title: t("contract.contractNumber"),
                    key: "contractNumber",
                    dataIndex: "contractNumber",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "contractNumber" ? sortInfo.order : null,
                },
                {
                    title: t("contract.acquirer"),
                    key: "acquirerName",
                    dataIndex: "acquirerName",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "acquirerName" ? sortInfo.order : null,
                },
                {
                    title: t("contract.appProfile"),
                    key: "applicationsProfileName",
                    dataIndex: "applicationsProfileName",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "applicationsProfileName" ? sortInfo.order : null,
                },
                {
                    title: t("contract.contractProfile"),
                    key: "contractProfileName",
                    dataIndex: "contractProfileName",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "contractProfileName" ? sortInfo.order : null,
                },
                {
                    title: t("contract.organizationUnit.name"),
                    key: "siteName",
                    dataIndex: "siteName",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "siteName" ? sortInfo.order : null,
                },
                {
                    title: t("contract.terminalType"),
                    key: "terminalType",
                    dataIndex: "terminalType",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "terminalType" ? sortInfo.order : null,
                },
                {
                    title: t("contract.contractCardType"),
                    key: "contractType",
                    dataIndex: "contractType",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "contractType" ? sortInfo.order : null,
                    render: (value) => displayEnumValue(value, "contract-base"),
                },
                {
                    title: t("contract.validFrom"),
                    key: "validFrom",
                    dataIndex: "validFrom",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "validFrom" ? sortInfo.order : null,
                    render: (value) => displayDateValue(value),
                },
                {
                    title: t("contract.validTo"),
                    key: "validTo",
                    dataIndex: "validTo",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "validTo" ? sortInfo.order : null,
                    render: (value) => displayDateValue(value),
                },
                {
                    title: t("contract.changed"),
                    dataIndex: "changed",
                    key: "changed",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "changed" ? sortInfo.order : null,
                    render: (value) => displayDateTimeValue(value),
                },
            ];
        },
        [t],
    );

    const navigate = useNavigate();
    const onRowClick = (record: ContractList) => {
        const route =
            record.contractType === "PHYSICAL"
                ? `${BASE_PATH}/${ResourceName.CONTRACTS}/${ResourceVariant.PHYSICAL}/${FORM_ACTION.EDIT}/${record.id}?discriminator=PHYSICAL&service=true&id=${record.id}`
                : `${BASE_PATH}/${ResourceName.CONTRACTS}/${ResourceVariant.VIRTUAL}/${FORM_ACTION.EDIT}/${record.id}?discriminator=VIRTUAL&service=true&id=${record.id}`;
        navigate(route);
    };

    return (
        (datasource && (
            <GenericTable<ContractList>
                getColumnsConfig={getColumnsConfig}
                datasource={datasource}
                name="contracts_table"
                onSelectionCallback={onRowSelectionChange}
                onStateChanged={onStateChanged}
                onRow={(record: ContractList) => {
                    return {
                        onClick: () => onRowClick(record),
                    };
                }}
                resource={resource}
            />
        )) ||
        null
    );
};
